import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { invitationApi, Invitation } from "../../services/invitationApi";
import { InvitationsList } from "../../components/admin/InvitationsList";
import { getTranslatedError } from "../../utils/errorUtils";
import { useAuth } from "../../context/AuthContext";
import { FaUserPlus } from "react-icons/fa";

export const Invitations: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newInvitationEmail, setNewInvitationEmail] = useState("");
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [resendingId, setResendingId] = useState<string | undefined>(undefined);
  const [cancellingId, setCancellingId] = useState<string | undefined>(undefined);

  // Only store_admin can manage invitations
  const isStoreAdmin = user?.role === "store_admin";

  useEffect(() => {
    if (isStoreAdmin) {
      fetchInvitations();
    }
  }, [isStoreAdmin]);

  const fetchInvitations = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await invitationApi.getInvitations();
      setInvitations(response.invitations || []);
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.fetch"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (): Promise<void> => {
    if (!newInvitationEmail.trim()) {
      setError(t("admin.invitations.errors.emailRequired"));
      return;
    }

    try {
      setCreatingInvitation(true);
      setError(null);
      await invitationApi.createInvitation(newInvitationEmail.trim());
      setNewInvitationEmail("");
      await fetchInvitations();
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.create"));
    } finally {
      setCreatingInvitation(false);
    }
  };

  const handleResendInvitation = async (id: string): Promise<void> => {
    try {
      setResendingId(id);
      setError(null);
      await invitationApi.resendInvitation(id);
      await fetchInvitations();
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.resend"));
    } finally {
      setResendingId(undefined);
    }
  };

  const handleCancelInvitation = async (id: string): Promise<void> => {
    try {
      setCancellingId(id);
      setError(null);
      await invitationApi.cancelInvitation(id);
      await fetchInvitations();
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.invitations.errors.cancel"));
    } finally {
      setCancellingId(undefined);
    }
  };

  if (!isStoreAdmin) {
    return (
      <Container>
        <Alert variant="error">{t("admin.invitations.errors.unauthorized")}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="lg">{t("admin.invitations.title")}</Heading>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.invitations.inviteCoAdmin")}</Heading>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t("admin.invitations.emailPlaceholder")}
                value={newInvitationEmail}
                onChange={(e) => setNewInvitationEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleCreateInvitation();
                  }
                }}
                className="flex-1"
              />
              <Button
                variant="primary"
                onClick={handleCreateInvitation}
                disabled={creatingInvitation || !newInvitationEmail.trim()}
              >
                {creatingInvitation ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    {t("admin.invitations.inviting")}
                  </>
                ) : (
                  <>
                    <FaUserPlus className="mr-2" />
                    {t("admin.invitations.sendInvitation")}
                  </>
                )}
              </Button>
            </div>
          </Stack>
        </Card>

        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.invitations.invitationsList")}</Heading>
            <InvitationsList
              invitations={invitations}
              loading={loading}
              onResend={handleResendInvitation}
              onCancel={handleCancelInvitation}
              resendingId={resendingId}
              cancellingId={cancellingId}
            />
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};
